<template>
  <NTable
    :data="transformedData"
    cell-padding="small"
    :column-splitter="false"
    fixed-header
    :striped="false"
    class="m-2"
    :sort-by="{
      prop: 'pricePercentage',
      order: 'descending',
    }"
    @row-click="(row) => updateActiveIsin(row.isin)"
  >
    <template #hoveredElements="{ row }">
      <NPopper hover offset-y="5" placement="top">
        <NegarIndustryIcon
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'industry',
            component: 'industryGroupsIcon',
          }"
          size="20"
          @click="openIndustryGroups"
        />

        <template #content>
          <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
            <NText
              weight="medium"
              variant="caption2"
              class="text-neutral-quaternary"
            >
              {{ t('symbol.industryGroups') }}
            </NText>
          </div>
        </template>
      </NPopper>

      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'industry',
          component: 'buyButton',
        }"
        size="mini"
        variant="fill"
        @click.stop="handleBuy(row)"
      >
        <NText variant="overline" weight="medium">
          {{ t('common.buy') }}
        </NText>
      </NButton>

      <NButton
        v-gid="{
          module: 'order',
          screen: 'home',
          section: 'industry',
          component: 'sellButton',
        }"
        size="mini"
        color="red"
        :disabled="offersIsin?.includes(row.isin)"
        variant="fill"
        class="mr-1"
        @click.stop="
          openNewOrderFormDialog({
            type: ORDER_TYPES.sell,
            symbol: { ...row, instrumentName: row.name },
          })
        "
      >
        <NText variant="overline" weight="medium">
          {{ t('common.sell') }}
        </NText>
      </NButton>
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.watchlist.symbol_name')"
      prop="name"
      class="w-28"
      sortable
      align="text-right"
    >
      <span class="inline-flex justify-center items-center">
        <SymbolTradeStateIcon
          class="me-1"
          :trading-state-code="row.instrumentTradingState"
        />
        <NText :key="row.name" variant="caption2" weight="medium">
          {{ row.name }}
        </NText>
      </span>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="PriceColumnLabel"
      prop="pricePercentage"
      class="w-28 inline-flex items-center"
      align="text-right"
      sortable
    >
      <!-- todo: use token color -->
      <span
        class="inline-flex items-center px-1 rounded-md"
        :class="{
          'bg-[#ffd420] bg-opacity-[15%] darkTheme:bg-opacity-10 darkTheme:bg-[#ffe449]':
            row.instrumentTradingState === TRADING_STATE_CODES.preOpening,
        }"
      >
        <template
          v-if="row.instrumentTradingState === TRADING_STATE_CODES.preOpening"
        >
          <NumberFormatter
            #="{ number }"
            :value="row.topVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="bold"
              :class="{
                'text-brand-primary': row.topVariationPercentage > 0,
                'text-critical-primary': row.topVariationPercentage < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.theoricalOpeningPrice">
            <ChangePriceEffect :price="row.theoricalOpeningPrice">
              <NText
                :key="row.theoricalOpeningPrice"
                variant="caption2"
                weight="bold"
              >
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </template>

        <template v-else>
          <NumberFormatter
            #="{ number }"
            :value="row.referencePriceVariationPercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="bold"
              :class="{
                'text-brand-primary': row.referencePriceVariationPercentage > 0,
                'text-critical-primary':
                  row.referencePriceVariationPercentage < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.lastTradePrice">
            <ChangePriceEffect :price="row.lastTradePrice">
              <NText :key="row.lastTradePrice" variant="caption2" weight="bold">
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </template>
      </span>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="`%${t('misc.final')}`"
      prop="closingPriceVariationPercentage"
      sortable
    >
      <NumberFormatter
        #="{ number }"
        :value="row.closingPriceVariationPercentage / 100"
        number-style="percent"
        maximum-fraction-digits="2"
      >
        <NText
          dir="ltr"
          variant="caption2"
          weight="bold"
          :class="{
            'text-brand-primary': row.closingPriceVariationPercentage > 0,
            'text-critical-primary': row.closingPriceVariationPercentage < 0,
          }"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  InitialPublicOfferItem,
  InstrumentSectorDetail,
} from '@libs/api-reference';
import { NegarIndustryIcon, NegarInfoWithBorderIcon } from '@negar/icons';
import { ORDER_TYPES } from '@core/order';
import { TRADING_STATE_CODES } from '@core/symbol';
import { computed, h } from 'vue';
import { NText } from '@negar/core';

import { useIpoDialogs } from '@/modules/ipo';
import { useOrderDialogs } from '@/modules/order';
import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';
import { useWatchlistDialogs } from '@/modules/watchlist';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

import { openIndustryGroups } from '@/composables/event/eventBus';

const props = defineProps<{
  data: InstrumentSectorDetail[];
  offersIsin?: (string | null)[];
  offers?: InitialPublicOfferItem[];
}>();

const { t } = useI18n();

const { openNewOrderFormDialog } = useOrderDialogs();
const { openWatchlistPriceDescriptionDialog } = useWatchlistDialogs();
const { openBuyOfferDialog } = useIpoDialogs();

const { updateActiveIsin } = useActiveSymbolIsin();

const handleBuy = (row: InstrumentSectorDetail) => {
  const offer = props.offers?.find(
    (offer) => offer.instrumentIsin === row.isin
  );

  if (offer) {
    openBuyOfferDialog({ offer });
  } else {
    openNewOrderFormDialog({
      type: ORDER_TYPES.buy,
      symbol: { ...row, instrumentName: row.name },
    });
  }
};

//TODO: move this logic to backend
const transformedData = computed(() =>
  props.data.map(
    (item: InstrumentSectorDetail & { pricePercentage?: number }) => {
      let pricePercentage = Number(item.referencePriceVariationPercentage);

      if (item.instrumentTradingState === TRADING_STATE_CODES.preOpening) {
        pricePercentage = item.topVariationPercentage;
      }

      item.pricePercentage = pricePercentage;

      return item;
    }
  )
);

const PriceColumnLabel = h(
  'span',
  {
    class: 'inline-flex items-center align-middle',
  },
  [
    h(NegarInfoWithBorderIcon, {
      size: 14,
      class: 'me-0.5',
      'data-gid': 'watchlist_home_industry_priceInfo',
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        openWatchlistPriceDescriptionDialog();
      },
    }),
    h(
      NText,
      {
        weight: 'bold',
        variant: 'overline',
      },
      [t('common.price')]
    ),
  ]
);
</script>
